export type BrowserSessionMode = 'shared' | 'isolated' | 'private';
export type BrowserSession = {
  id: string;
  name: string;
  initialUrl?: string;
  mode: BrowserSessionMode;
  profileId?: string;
  /** A named isolated profile (an agent's `{ session: 'isolated', profile }`), kept for good. */
  profile?: string;
  /** The page's favicon, shown on its tab. */
  favicon?: string;
};

/** The name of a browser tab before it shows a page. */
export const NEW_BROWSER_TAB_NAME = 'New tab';

/** Creates a new, blank browser session with a unique id: it opens on its address field. */
export function createBrowserSession(mode: BrowserSessionMode = 'shared'): BrowserSession {
  return {
    id: `browser-${crypto.randomUUID()}`,
    name: NEW_BROWSER_TAB_NAME,
    mode,
    ...(mode === 'shared' ? {} : { profileId: crypto.randomUUID() }),
  };
}

/** Creates a browser session that opens directly at a specific URL. */
export function createBrowserSessionAt(
  name: string,
  initialUrl: string,
  {
    id = `browser-${crypto.randomUUID()}`,
    mode = 'shared',
    profile,
  }: {
    id?: string | undefined;
    mode?: BrowserSessionMode | undefined;
    /** A named isolated profile, the same one every time (an isolated `mode` only). */
    profile?: string | undefined;
  } = {},
): BrowserSession {
  return {
    id,
    name,
    initialUrl,
    mode,
    ...(mode === 'shared' || profile !== undefined ? {} : { profileId: crypto.randomUUID() }),
    ...(mode === 'isolated' && profile !== undefined ? { profile } : {}),
  };
}

/** The profiles a browser tab can use, as its session menu offers them. */
export const BROWSER_SESSION_MODES: readonly {
  mode: BrowserSessionMode;
  label: string;
  description: string;
}[] = [
  { mode: 'shared', label: 'Shared session', description: 'Cookies and logins of the other tabs' },
  {
    mode: 'isolated',
    label: 'Isolated session',
    description: 'Its own cookies, kept for this tab',
  },
  { mode: 'private', label: 'Private', description: 'Nothing kept once the tab closes' },
];

/**
 * The tab moved to another profile: an isolated or private one is new each time, and a private
 * tab keeps no favicon. The page stays the one the tab shows.
 */
export function withBrowserSessionMode(
  session: BrowserSession,
  mode: BrowserSessionMode,
): BrowserSession {
  if (session.mode === mode) return session;
  const { profileId: _profileId, profile: _profile, favicon, ...rest } = session;
  return {
    ...rest,
    mode,
    ...(mode === 'shared' ? {} : { profileId: crypto.randomUUID() }),
    ...(mode !== 'private' && favicon !== undefined ? { favicon } : {}),
  };
}

/** Makes isolated/private profiles visible without changing their page-derived names. */
export function browserSessionDisplayName(session: BrowserSession): string {
  if (session.profile !== undefined) return `${session.profile} · ${session.name}`;
  if (session.mode === 'isolated') return `Isolated · ${session.name}`;
  if (session.mode === 'private') return `Private · ${session.name}`;
  return session.name;
}
